import { useEffect, useRef, useState } from "react";
import type { InvitationEmailState, InvitationEmailStatus } from "@baton/contracts";
import { ApiError, type DashboardApi } from "./api.js";
import type { PersonRow } from "./people.js";

export const EMAIL_STATUS_LABELS: Record<InvitationEmailState, string> = {
  queued: "Email queued (retries are automatic).",
  sending: "Sending email…",
  sent: "Email accepted by Gmail. Delivery and reading are not confirmed.",
  failed: "Email could not be sent after three attempts. Check the address or share manually.",
  cancelled: "Email cancelled: the invitation or access is no longer valid, or the queue expired.",
};

/** Explicit recipient entry: a GitHub handle must never be guessed to be an email address. */
export function InvitationEmailForm({ api, projectId, rows }: { api: DashboardApi; projectId: string; rows: PersonRow[] }) {
  const [status, setStatus] = useState<InvitationEmailStatus | null>(null);
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Retain the same request key after ambiguous network failures, but not after a confirmed response.
  const pending = useRef<{ email: string; userId: string; key: string } | null>(null);
  const generation = useRef(0);
  const people = rows.filter((row) => row.state === "invited");
  const selectedId = people.some((person) => person.userId === userId) ? userId : people[0]?.userId ?? "";
  const latest = status?.emails.find((entry) => entry.userId === selectedId);

  useEffect(() => {
    const current = ++generation.current;
    let timer: ReturnType<typeof setTimeout>;
    setStatus(null); setError(null); setEmail(""); setUserId(""); setBusy(false); pending.current = null;
    async function refresh() {
      try {
        const next = await api.invitationEmails(projectId);
        if (generation.current !== current) return;
        setStatus(next);
      } catch (caught) {
        if (generation.current !== current) return;
        if (caught instanceof ApiError && (caught.status === 404 || caught.status === 503)) {
          // Safe during frontend-first rolling deployments, or an unconfigured server.
          setStatus({ configured: false, sender: null, emails: [] });
        } else setError("Could not refresh email status. Reopen this dialog to try again.");
      }
      if (generation.current === current) timer = setTimeout(() => void refresh(), 5_000);
    }
    void refresh();
    return () => { generation.current += 1; clearTimeout(timer); };
  }, [api, projectId]);

  async function send(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedId || !email.trim() || busy) return;
    const current = generation.current;
    const address = email.trim().toLowerCase();
    if (pending.current?.email !== address || pending.current.userId !== selectedId) {
      pending.current = { email: address, userId: selectedId, key: crypto.randomUUID() };
    }
    setBusy(true); setError(null);
    try {
      const result = await api.emailProjectInvitation(projectId, selectedId, address, pending.current.key);
      if (generation.current !== current) return;
      pending.current = null;
      setEmail("");
      setStatus((value) => value ? { ...value, emails: [...value.emails.filter((entry) => entry.userId !== result.userId), result] } : value);
    } catch (caught) {
      if (generation.current !== current) return;
      setError(caught instanceof Error ? caught.message : "Could not queue the email. The in-app invitation is still pending.");
    } finally { if (generation.current === current) setBusy(false); }
  }

  return <section data-testid="invitation-email-form">
    <h4>Email a pending invitation</h4>
    <p className="muted compact">Create an invitation above, then enter that person’s email address. They must sign in with the invited GitHub account; email alone never grants access.</p>
    {status === null ? <p className="muted compact">Checking email availability…</p>
      : !status.configured ? <p role="status">Email sending is not configured yet. The in-app invitation still works; share a link manually below.</p>
        : <>
          <p className="muted compact">From: Baton Notifications &lt;{status.sender}&gt;</p>
          {people.length === 0 ? <p>No pending invitations to email.</p> : <form className="invite-form invitation-email-fields" onSubmit={(event) => void send(event)}>
            <label>Invited GitHub user
              <select value={selectedId} disabled={busy} onChange={(event) => { setUserId(event.target.value); setEmail(""); setError(null); }}>
                {people.map((person) => <option key={person.userId} value={person.userId}>@{person.githubLogin ?? person.handle}</option>)}
              </select>
            </label>
            <label>Recipient email
              <input type="email" autoComplete="off" required maxLength={254} value={email} disabled={busy}
                placeholder="collaborator@example.com" onChange={(event) => setEmail(event.target.value)} />
            </label>
            <button type="submit" className="primary" disabled={busy || latest?.state === "queued" || latest?.state === "sending"}>
              {busy ? "Queueing…" : latest ? "Resend email" : "Send email"}
            </button>
          </form>}
        </>}
    {latest && <p role="status">{EMAIL_STATUS_LABELS[latest.state]}</p>}
    {error && <p className="error" role="alert">{error}</p>}
  </section>;
}
